<template>
  <div class="radio-container">
    <span
      class="radio"
      aria-disabled="false"
      :class="{
        'with-label': !!label,
        disabled: disabled,
        checked: isChecked,
        focused: isFocused && !isChecked,
        hovered: hovered && !isChecked && !isFocused
      }"
    >
      <span class="radio-icon">
        <input
          :id="id"
          :name="name"
          type="radio"
          :disabled="disabled"
          :value="value"
          v-model="model"
          @focus="isFocused = true"
          @blur="isFocused = false"
          @mouseover="hovered = true"
          @mouseleave="hovered = false"
        />
        <div class="dot">
          <n-icon v-show="isChecked" class="dot-icon" icon="dot" size="m" />
        </div>
      </span>
    </span>
    <label :for="id" class="label">
      {{ label }}
      <n-tooltip v-if="info" :tippyProps="{ content: info }" class="info-icon">
        <n-icon icon="information-circle" />
      </n-tooltip>
    </label>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { uuid } from 'vue-uuid'

@Component({
  model: { prop: 'selection', event: 'selected' }
})
export default class NRadio extends Vue {
  @Prop() selection!: any
  @Prop() value!: any
  @Prop({ type: String }) name?: string
  @Prop({ type: String, default: '' }) label!: String
  @Prop({ type: Boolean, default: false }) disabled!: boolean
  @Prop({ type: String, default: '' }) info!: String

  id = uuid.v4()
  isFocused = false
  hovered = false

  get isChecked() {
    return this.value === this.selection
  }

  get model() {
    return this.selection
  }

  set model(value) {
    this.$emit('selected', value)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.radio-container {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  .radio {
    flex: 0 0 auto;
    color: $notch-color-neutral-grey-000;
    width: $notch-space-4x;
    height: $notch-space-4x;
    overflow: visible;
    border-radius: $notch-radius-full;
    border: 2px solid $notch-color-neutral-sand-200;
    position: relative;
    &.with-label {
      margin-right: $notch-space-3x;
    }

    &.checked {
      color: $notch-color-neutral-grey-900;
      border-color: $notch-color-neutral-grey-900;

      &.disabled {
        color: $notch-color-neutral-grey-600;
        border-color: $notch-color-neutral-grey-600;
      }
    }
    &.focused {
      box-shadow: 0px 0px 0px 2px $notch-color-semantic-accent-500;
    }
    &.hovered {
      border-color: $notch-color-neutral-sand-400;
    }

    &.disabled {
      cursor: not-allowed;
    }
    &-icon {
      width: 100%;
      display: flex;
      align-items: inherit;
      justify-content: inherit;
      input {
        cursor: inherit;
        margin: 0;
        opacity: 0;
        padding: 0;
        z-index: $notch-layer-1;
      }

      .dot {
        display: flex;

        &-icon {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }
  }
}

.label {
  @include notch-text-label-light-M;
  cursor: pointer;
  display: flex;
  align-items: center;

  .info-icon {
    margin-left: $notch-space-2x;
    color: $notch-color-neutral-sand-600;
  }
}
</style>
